import { createContext, useContext } from 'react'
import type { OperatorRole, RolesByChain } from '@/types/api'

export type SessionStatus =
    | 'disconnected'
    | 'connecting'
    | 'checking-role'
    | 'no-role'
    | 'signing-in'
    | 'session-failed'
    | 'ready'

export interface OperatorSessionContextValue {
    status: SessionStatus
    address?: `0x${string}`
    // PlayerRegistry.OPERATOR_ROLE membership per chain. This is the on-chain
    // *write* permission (gates register/suspend/ban etc.), NOT console access;
    // it is shown per chain on the home and settings pages. Decoupled from sign-in
    // eligibility. The lifetime-EUR action gates on BlockpotOperator's
    // OPERATOR_ROLE instead (useBlockpotOperatorRole).
    rolesByChain: Record<number, boolean>
    // OperatorRoles.hasAnyConsoleRole per chain — the console sign-in gate.
    consoleEligibilityByChain: Record<number, boolean>
    // Per-chain console RBAC from the JWT (chain-sourced cache). `roles` is the
    // flattened union used by the global JWT-based gates.
    consoleRolesByChain: RolesByChain
    eligibleChainIds: number[]
    sessionChainIds: number[]
    roles: OperatorRole[]
    signInError?: string
    retrySignIn: () => void
    signOut: () => void
}

export const OperatorSessionContext = createContext<OperatorSessionContextValue | undefined>(
    undefined,
)

export function useOperatorSession(): OperatorSessionContextValue {
    const ctx = useContext(OperatorSessionContext)
    if (!ctx) throw new Error('useOperatorSession must be used inside OperatorSessionProvider')
    return ctx
}
